import { useEffect, useMemo, useState } from 'react';
import DeviceManager from '@/components/util/deviceManager';

export const useSearchbarStyles = ({
  showSkeleton,
  wrapperWidth,
  isBackgroundVisible,
  popupOpen,
  mobilePosition,
  filteredItems,
  rtl,
  searchText,
  selectedCategory,
  navigationSharingPopupOpen,
  actionPathFinished,
}) => {
  const searchWrapperStyle = useMemo(() => {
    return {
      width: {
        xs: '100%',
        //  `calc(${wrapperWidth} - 28px)`
        lg: showSkeleton ? `calc(${wrapperWidth} + 30px)` : wrapperWidth,
      },
      ...(!isBackgroundVisible && {
        position: 'absolute',
        top: { xs: 43, lg: 80 },
        left: 0,
        right: 0,
        mx: 'auto',
      }),
      mx: 'auto',
      transition: 'width 0.3s ease-in-out',
      pointerEvents: 'auto',
      zIndex: 10,
    };
  }, [isBackgroundVisible, showSkeleton, wrapperWidth]);

  const searchTitleWrapperStyle = useMemo(() => {
    return {
      width: '100%',
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'center',
      maxHeight: '48px',
      height: '100%',
      minHeight: {
        xs: (!!searchText || !!selectedCategory) && mobilePosition > 1 ? '30px' : '0px',
        sm: '0px',
      },
    };
  }, [mobilePosition, searchText, selectedCategory]);

  const menuButtonStyle = useMemo(() => {
    return {
      minWidth: 'unset',
      width: '44px',
      height: '44px',
      background: '#fff',
      borderRadius: '8px',
      display: { xs: 'none', lg: 'inline-flex' },
      '&:hover': {
        background: '#fff',
      },
    };
  }, []);

  const clearSearchButtonStyle = useMemo(() => {
    return {
      minWidth: 'unset',
      width: '44px',
      height: '44px',
      background: '#fff',
      borderRadius: '8px',
      display: { xs: 'none', lg: 'inline-flex' },
      '&:hover': {
        background: '#fff',
      },
    };
  }, []);

  const searchTextFieldStyle = useMemo(() => {
    return {
      width: '100%',
      '& .MuiInputBase-input': {
        fontSize: '18px',
        fontFamily: 'Inter, Heebo, sans-serif',
      },
      '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
      ...(DeviceManager.isMobile() && {
        input: {
          '&::placeholder': {
            color: '#98A2B3',
            fontSize: '18px',
            fontStyle: 'normal',
            fontWeight: rtl ? 500 : 600,
            lineHeight: 'normal',
            direction: rtl ? 'rtl' : 'ltr',
            fontFamily: 'Inter, Heebo, sans-serif',
          },
        },
      }),
      fieldset: { height: '100%', top: 0, border: 'none' },
      mb: { xs: '10px', sm: '10px' },
      mt: { xs: '10px', sm: '48px' },
      pointerEvents: 'auto',
    };
  }, [rtl]);

  const resultsMainWrapperStyle = useMemo(() => {
    return {
      // position: { xs: 'absolute', sm: 'unset' },
      // top: '117px',
      width: { xs: wrapperWidth, lg: showSkeleton ? `calc(${wrapperWidth} + 30px)` : wrapperWidth },
      flexWrap: 'wrap',
      display: {
        xs:
          mobilePosition === 0 || (mobilePosition === 1 && filteredItems?.length > 3) || popupOpen
            ? 'none'
            : 'flex',
        sm: 'flex',
      },
      visibility: popupOpen ? 'hidden' : 'visible',
      zIndex: 1000,
      mx: 'auto',
      borderRadius: '8px',
      background: { xs: 'transparent', sm: '#fff' },
      border: { xs: 'none', lg: '1px solid #D0D5DD' },
      animation: 'fade-in 0.5s ease-in-out forwards',
      '@keyframes fade-in': {
        '0%': {
          opacity: 0,
        },
        '100%': {
          opacity: 1,
        },
      },
    };
  }, [wrapperWidth, showSkeleton, mobilePosition, filteredItems?.length, popupOpen]);

  const resultsSecondWrapperStyle = useMemo(() => {
    return {
      width: '100%',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      height: '72px',
      px: { xs: '0px', sm: '20px' },
      display: { xs: !searchText?.length && !selectedCategory ? 'none' : 'flex', sm: 'flex' },
    };
  }, [searchText?.length, selectedCategory]);

  const chevronWrapperStyle = useMemo(() => {
    return {
      display: { xs: 'none', sm: 'flex' },
      width: '44px',
      height: '44px',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '8px',
      background: '#FFF',
      cursor: 'pointer',
      border: '1px solid #EAECF0',
    };
  }, []);

  const calculateHeight = () => `${0.95 * window.innerHeight - 250}px`;

  const [dynamicHeight, setDynamicHeight] = useState(calculateHeight);

  useEffect(() => {
    const handleResize = () => {
      setDynamicHeight(calculateHeight());
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const itemsWrapperStyle = useMemo(() => {
    return {
      gap: { xs: mobilePosition > 1 ? 0 : '10px', sm: 0 },
      width: { xs: mobilePosition > 1 ? '100vw' : '100%', sm: '100%' },
      flexDirection: 'row',
      flexWrap: { xs: mobilePosition > 1 ? 'wrap' : 'nowrap', sm: 'wrap' },
      background: 'transparent', // { xs: mobilePosition > 1 ? '#fff' : 'transaprent', sm: '#fff' },
      maxHeight: { xs: dynamicHeight, sm: '410px' },
      overflowY: 'auto',
      '&::-webkit-scrollbar': {
        width: '4px',
        backgroundColor: 'transparent',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'transparent',
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: 'transparent',
      },
      '&:hover': {
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#BDBDBD',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#fff',
        },
      },
      overflowX: {
        xs: filteredItems?.length > 3 && mobilePosition < 2 ? 'auto' : 'hidden',
        sm: 'hidden',
      },
      justifyContent: {
        xs: filteredItems?.length > 3 && mobilePosition < 2 ? 'flex-start' : 'center',
        sm: 'center',
      },
      marginLeft: { xs: mobilePosition > 1 ? '-14px' : 'unset', sm: 'unset' },
    };
  }, [mobilePosition, filteredItems?.length, dynamicHeight]);

  const itemStyle = useMemo(() => {
    return {
      width: '100%',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      height: { xs: mobilePosition < 2 ? '117px' : 'auto', sm: 'auto' },
      px: { xs: '14px', sm: '20px' },
      py: { xs: mobilePosition > 1 ? '14px' : 'unset', sm: '18.145px' },
      display: 'flex !important',
      borderTop: {
        xs: mobilePosition > 1 ? '1px solid #EAECF0 !important' : 'none',
        lg: '1px solid #D0D5DD',
      },
      borderBottom: {
        xs: mobilePosition > 1 ? '1px solid #EAECF0 !important' : 'none',
        lg: 'none',
      },
      cursor: 'pointer',
      border: { xs: mobilePosition < 2 ? '1px solid #EAECF0' : 'none', sm: 'none' },
      borderRadius: { xs: mobilePosition < 2 ? '8px' : '0px', sm: '0px' },
      background: '#FFF',
      // minWidth: { xs: '114px', sm: 'unset' },
      maxWidth: {
        xs: filteredItems?.length > 3 && mobilePosition < 2 ? '114px' : 'unset',
        sm: 'unset',
      },
      '&:hover': {
        background: 'rgba(32, 85, 255, 0.06)',
      },
    };
  }, [filteredItems?.length, mobilePosition]);

  const itemInnerStyle = useMemo(() => {
    return {
      flexDirection: { xs: mobilePosition < 2 ? 'column' : 'row', sm: 'row' },
      alignItems: 'center',
      flex: 1,
      boxSizing: 'border-box',
      gap: { xs: '10px', sm: 'unset' },
    };
  }, [mobilePosition]);

  const itemIconStyle = useMemo(() => {
    return {
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      background: 'transparent',
      borderRadius: '8px',
      p: 0,
      mr: { xs: 0, sm: rtl ? '0' : '14px' },
      ml: { xs: 0, sm: rtl ? '14px' : '0' },
      border: '1px solid #E8E8E8',
      flexShrink: 0,
    };
  }, [rtl]);

  const itemChevronStyle = useMemo(() => {
    return {
      display: { xs: 'none', sm: 'flex' },
      flexDirection: 'row',
      gap: '10px',
      alignItems: 'center',
      width: '128px',
      justifyContent: 'flex-end',
      flex: 1,
      boxSizing: 'border-box',
    };
  }, []);

  const choicePopupWrapper = useMemo(() => {
    return {
      padding: '0px',
      width: DeviceManager.isMobile() ? '100%' : '340px',
      overflow: 'hidden',
      background: '#F7F7F7',
      transform: 'none',
      position: 'static',
      animation: 'none',
      marginLeft: DeviceManager.isMobile() ? 0 : '',
    };
  }, []);

  const choicePopupSearchBar = useMemo(() => {
    return {
      width: '100%',
      height: '50px',
      justifyContent: 'center',
      gap: '10px',
      background: '#ffffff',
      borderRadius: '10px',
      border: '1px solid #D0D5DD',
      fieldset: { border: 'none' },
      '& .MuiInputBase-input': {
        fontSize: '16px',
        fontWeight: 400,
      },
    };
  }, []);

  const choicePopupFilteredBtn = useMemo(() => {
    return {
      width: '100%',
      display: 'flex',
      justifyContent: 'flex-start',
      height: '74px',
      padding: '18px 14px',
      gap: '10px',
      borderBottom: '1px solid #E8E8E8',
      color: '#101828',
    };
  }, []);

  const popupsContainerStyle = useMemo(
    () => ({
      position: DeviceManager.isMobile() ? 'unset' : 'fixed',
      maxHeight: `calc(95vh - ${DeviceManager.isMobile() ? 63 : 70}px - 20px)`,
      height: {
        xs: navigationSharingPopupOpen && actionPathFinished ? '30dvh' : '100dvh',
        sm: 'fit-content',
      },
      overflow: 'auto',
      width: { xs: '100%', sm: 'fit-content' },
      marginTop: { xs: '0', sm: '20px' },
      marginLeft: { xs: '0', sm: '20px' },
      backgroundColor: '#fff',
      borderRadius: '14px',
      zIndex: 100,
      '&::-webkit-scrollbar': {
        background: 'transparent',
      },
      '&::-webkit-scrollbar-thumb': {
        width: '6px',
        backgroundColor: '#BDBDBD',
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: '#F7F7F7',
      },
    }),
    [navigationSharingPopupOpen, actionPathFinished],
  );

  return {
    searchWrapperStyle,
    searchTitleWrapperStyle,
    menuButtonStyle,
    clearSearchButtonStyle,
    searchTextFieldStyle,
    resultsMainWrapperStyle,
    resultsSecondWrapperStyle,
    chevronWrapperStyle,
    itemsWrapperStyle,
    itemStyle,
    itemIconStyle,
    itemChevronStyle,
    itemInnerStyle,
    choicePopupWrapper,
    choicePopupSearchBar,
    choicePopupFilteredBtn,
    popupsContainerStyle,
  };
};

export const mobileCloseButtonStyle = {
  position: 'absolute',
  top: { xs: '14px', sm: '10px' },
  right: { xs: '14px', sm: '10px' },
  width: { xs: '32px', sm: '30px' },
  height: { xs: '32px', sm: '30px' },
  borderRadius: '80px',
  background: '#EAECF0',
  boxShadow: '0px 0.727px 1.455px 0px rgba(16, 24, 40, 0.05)',
  minWidth: 'unset',
  zIndex: 9999,
  '&>i': {
    color: 'black',
  },
};
